/**
 * Text settings panel for Result Text and Custom Text
 */
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { GOOGLE_FONTS, FONT_WEIGHT_OPTIONS, TEXT_ALIGN_OPTIONS } from "./constants";
import { AlignLeft, AlignCenter, AlignRight } from "lucide-react";

/**
 * Result Text Settings (from lottery)
 */
export function ResultTextSettings({ banner, setBanner, resultText, customFonts = [] }) {
  // Handle font selection - store both font name and custom font ID
  const handleFontChange = (value) => {
    // Check if it's a custom font (has an ID)
    const customFont = customFonts.find(f => f.id === value);
    if (customFont) {
      setBanner({ 
        ...banner, 
        result_text_font: customFont.font_family,
        result_text_custom_font_id: customFont.id 
      });
    } else {
      setBanner({ 
        ...banner, 
        result_text_font: value,
        result_text_custom_font_id: null 
      });
    }
  };

  // Get current font value for the select
  const getCurrentFontValue = () => {
    if (banner.result_text_custom_font_id) {
      return banner.result_text_custom_font_id;
    }
    return banner.result_text_font;
  };

  return (
    <div className="border border-white/10 rounded-lg p-3 space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-[#10B981] uppercase tracking-wider font-semibold">Result Text</Label>
        <Switch
          checked={banner.show_result_text}
          onCheckedChange={(checked) => setBanner({ ...banner, show_result_text: checked })}
          data-testid="show-result-text-toggle"
          className="scale-75"
        />
      </div>
      {resultText ? (
        <p className="text-xs text-gray-400 italic">"{resultText}"</p>
      ) : (
        <p className="text-xs text-gray-500">No result text set for this lottery</p>
      )}
      
      {banner.show_result_text && resultText && (
        <div className="space-y-3 pt-2 border-t border-white/5">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-gray-500">Font</Label>
              <select
                value={getCurrentFontValue()}
                onChange={(e) => handleFontChange(e.target.value)}
                className="w-full mt-1 h-7 px-2 rounded bg-[#1a1a24] border border-white/10 text-xs text-white"
              >
                {GOOGLE_FONTS.map(font => (
                  <option key={font} value={font}>{font}</option>
                ))}
                {customFonts.length > 0 && (
                  <optgroup label="Custom Fonts">
                    {customFonts.map(font => (
                      <option key={font.id} value={font.id}>📁 {font.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Weight</Label>
              <select
                value={banner.result_text_font_weight || 700}
                onChange={(e) => setBanner({ ...banner, result_text_font_weight: parseInt(e.target.value) })}
                className="w-full mt-1 h-7 px-2 rounded bg-[#1a1a24] border border-white/10 text-xs text-white"
              >
                {FONT_WEIGHT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Text Alignment */}
          <div>
            <Label className="text-xs text-gray-500">Alignment</Label>
            <div className="flex gap-1 mt-1">
              {TEXT_ALIGN_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setBanner({ ...banner, result_text_align: opt.value })}
                  className={`flex-1 h-7 rounded flex items-center justify-center transition-colors ${
                    (banner.result_text_align || 'left') === opt.value 
                      ? 'bg-[#10B981] text-white' 
                      : 'bg-[#1a1a24] border border-white/10 text-gray-400 hover:text-white'
                  }`}
                  data-testid={`result-text-align-${opt.value}`}
                >
                  {opt.value === 'left' && <AlignLeft size={14} />}
                  {opt.value === 'center' && <AlignCenter size={14} />}
                  {opt.value === 'right' && <AlignRight size={14} />}
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <Label className="text-xs text-gray-500">Size: {banner.result_text_size}px</Label>
            <Slider
              value={[banner.result_text_size]}
              onValueChange={([value]) => setBanner({ ...banner, result_text_size: value })}
              min={10}
              max={120}
              step={1}
              className="mt-1"
            />
          </div>
          
          <div>
            <Label className="text-xs text-gray-500">Color</Label>
            <div className="flex gap-2 mt-1">
              <input
                type="color"
                value={banner.result_text_color}
                onChange={(e) => setBanner({ ...banner, result_text_color: e.target.value })}
                className="w-7 h-7 rounded cursor-pointer border-0"
              />
              <Input
                value={banner.result_text_color}
                onChange={(e) => setBanner({ ...banner, result_text_color: e.target.value })}
                className="flex-1 h-7 bg-[#1a1a24] border-white/10 text-white font-mono text-xs"
              />
            </div>
          </div>
          
          {/* Shadow Settings */}
          <div className="border-t border-white/5 pt-2">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-gray-500">Shadow</Label>
              <Switch
                checked={banner.result_text_shadow}
                onCheckedChange={(checked) => setBanner({ ...banner, result_text_shadow: checked })}
                className="scale-75"
              />
            </div>
            {banner.result_text_shadow && (
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] text-gray-500">X: {banner.result_text_shadow_x}</Label>
                  <Slider
                    value={[banner.result_text_shadow_x]}
                    onValueChange={([value]) => setBanner({ ...banner, result_text_shadow_x: value })}
                    min={0}
                    max={10}
                    step={1}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500">Y: {banner.result_text_shadow_y}</Label>
                  <Slider
                    value={[banner.result_text_shadow_y]}
                    onValueChange={([value]) => setBanner({ ...banner, result_text_shadow_y: value })}
                    min={0}
                    max={10}
                    step={1}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500">Blur: {banner.result_text_shadow_blur}</Label>
                  <Slider
                    value={[banner.result_text_shadow_blur]}
                    onValueChange={([value]) => setBanner({ ...banner, result_text_shadow_blur: value })}
                    min={0}
                    max={20}
                    step={1}
                    className="mt-1"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Custom Text Settings (banner-specific)
 */
export function CustomTextSettings({ banner, setBanner, customFonts = [] }) {
  // Handle font selection - store both font name and custom font ID
  const handleFontChange = (value) => {
    // Check if it's a custom font (has an ID)
    const customFont = customFonts.find(f => f.id === value);
    if (customFont) {
      setBanner({ 
        ...banner, 
        text_font: customFont.font_family,
        text_custom_font_id: customFont.id 
      });
    } else {
      setBanner({ 
        ...banner, 
        text_font: value,
        text_custom_font_id: null 
      });
    }
  };

  // Get current font value for the select
  const getCurrentFontValue = () => {
    if (banner.text_custom_font_id) {
      return banner.text_custom_font_id;
    }
    return banner.text_font;
  };

  return (
    <div className="border border-white/10 rounded-lg p-3 space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-amber-500 uppercase tracking-wider font-semibold" data-testid="custom-text-label">
          Custom Text
        </Label>
        <Switch
          checked={banner.show_custom_text}
          onCheckedChange={(checked) => setBanner({ ...banner, show_custom_text: checked, show_text: checked })}
          data-testid="show-text-toggle"
          className="scale-75"
        />
      </div>
      <Input
        value={banner.custom_text}
        onChange={(e) => setBanner({ ...banner, custom_text: e.target.value })}
        placeholder="Enter custom text..."
        data-testid="custom-text-input"
        className="h-8 bg-[#1a1a24] border-white/10 text-white placeholder:text-gray-600 text-sm"
      />
      
      {banner.show_custom_text && banner.custom_text && (
        <div className="space-y-3 pt-2 border-t border-white/5">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-gray-500">Font</Label>
              <select
                value={getCurrentFontValue()}
                onChange={(e) => handleFontChange(e.target.value)}
                className="w-full mt-1 h-7 px-2 rounded bg-[#1a1a24] border border-white/10 text-xs text-white"
              >
                {GOOGLE_FONTS.map(font => (
                  <option key={font} value={font}>{font}</option>
                ))}
                {customFonts.length > 0 && (
                  <optgroup label="Custom Fonts">
                    {customFonts.map(font => (
                      <option key={font.id} value={font.id}>📁 {font.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-500">Weight</Label>
              <select
                value={banner.text_font_weight || 700}
                onChange={(e) => setBanner({ ...banner, text_font_weight: parseInt(e.target.value) })}
                className="w-full mt-1 h-7 px-2 rounded bg-[#1a1a24] border border-white/10 text-xs text-white"
                data-testid="text-font-weight-select"
              >
                {FONT_WEIGHT_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          {/* Text Alignment */}
          <div>
            <Label className="text-xs text-gray-500">Alignment</Label>
            <div className="flex gap-1 mt-1">
              {TEXT_ALIGN_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setBanner({ ...banner, text_align: opt.value })}
                  className={`flex-1 h-7 rounded flex items-center justify-center transition-colors ${
                    (banner.text_align || 'left') === opt.value 
                      ? 'bg-amber-500 text-white' 
                      : 'bg-[#1a1a24] border border-white/10 text-gray-400 hover:text-white'
                  }`}
                  data-testid={`custom-text-align-${opt.value}`}
                >
                  {opt.value === 'left' && <AlignLeft size={14} />}
                  {opt.value === 'center' && <AlignCenter size={14} />}
                  {opt.value === 'right' && <AlignRight size={14} />}
                </button>
              ))}
            </div>
          </div>
          
          <div>
            <Label className="text-xs text-gray-500">Size: {banner.text_size}px</Label>
            <Slider
              value={[banner.text_size]}
              onValueChange={([value]) => setBanner({ ...banner, text_size: value })}
              min={10}
              max={120}
              step={1}
              className="mt-1"
            />
          </div>
          
          <div>
            <Label className="text-xs text-gray-500">Color</Label>
            <div className="flex gap-2 mt-1">
              <input
                type="color"
                value={banner.text_color}
                onChange={(e) => setBanner({ ...banner, text_color: e.target.value })}
                className="w-7 h-7 rounded cursor-pointer border-0"
              />
              <Input
                value={banner.text_color}
                onChange={(e) => setBanner({ ...banner, text_color: e.target.value })}
                className="flex-1 h-7 bg-[#1a1a24] border-white/10 text-white font-mono text-xs"
              />
            </div>
          </div>
          
          {/* Shadow Settings */}
          <div className="border-t border-white/5 pt-2">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs text-gray-500">Shadow</Label>
              <Switch
                checked={banner.text_shadow}
                onCheckedChange={(checked) => setBanner({ ...banner, text_shadow: checked })}
                className="scale-75"
              />
            </div>
            {banner.text_shadow && (
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-[10px] text-gray-500">X: {banner.text_shadow_x}</Label>
                  <Slider
                    value={[banner.text_shadow_x]}
                    onValueChange={([value]) => setBanner({ ...banner, text_shadow_x: value })}
                    min={0}
                    max={10}
                    step={1}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500">Y: {banner.text_shadow_y}</Label>
                  <Slider
                    value={[banner.text_shadow_y]}
                    onValueChange={([value]) => setBanner({ ...banner, text_shadow_y: value })}
                    min={0}
                    max={10}
                    step={1}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label className="text-[10px] text-gray-500">Blur: {banner.text_shadow_blur}</Label>
                  <Slider
                    value={[banner.text_shadow_blur]}
                    onValueChange={([value]) => setBanner({ ...banner, text_shadow_blur: value })}
                    min={0}
                    max={20}
                    step={1}
                    className="mt-1"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
